import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  Container,
  useTheme,
  TextField,
} from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";

const PLATFORM_OWNER_USER_ID = import.meta.env.VITE_PLATFORM_OWNER_USER_ID;
import AddClient from "./AddClient";
import Loading from "../Layout/Loading";
import ClientWorkoutCalendar from "./Client";
import ManageProductsDialog from "./ManageProductsDialog";
import CoachSubscriptionModal from "./CoachSubscriptionModal";
import CoachUpgradeModal from "./CoachUpgradeModal";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const appearance = {
  theme: "stripe",
  variables: {
    colorPrimary: "#ff0000",
    fontFamily: "Roboto Mono, monospace",
  },
};

const ClientsList = ({
  clients,
  coachProfile,
  activeClient,
  setActiveClient,
  onAddClientClick,
  theme,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = React.useRef();

  const filteredClients = React.useMemo(() => {
    const allClients = coachProfile ? [coachProfile, ...clients] : clients;
    return allClients.filter(
      (client) =>
        client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        client.email.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [clients, coachProfile, searchQuery]);

  React.useEffect(() => {
    if (filteredClients.length > 0) {
      if (
        !activeClient ||
        !filteredClients.find((c) => c.id === activeClient.id)
      ) {
        setActiveClient(filteredClients[0]);
      }
    } else if (clients.length > 0 && searchQuery === "") {
      setActiveClient(clients[0]);
    } else {
      setActiveClient(null);
    }
  }, [filteredClients]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2 }}>
        <Button onClick={onAddClientClick} fullWidth sx={{ mb: 2 }}>
          Add Client
        </Button>
        <TextField
          inputRef={searchInputRef}
          fullWidth
          size="small"
          placeholder="Search clients..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
        />
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        {filteredClients.length ? (
          filteredClients.map((client, index) => {
            const isCoach = coachProfile && client.id === coachProfile.id;
            return (
              <Paper
                sx={{
                  mx: 2,
                  mb: 2,
                  p: 2,
                  backgroundColor:
                    activeClient?.id === client.id
                      ? theme.palette.primary.main
                      : isCoach
                      ? "#f0f0f0"
                      : "white",
                  color: activeClient?.id === client.id ? "white" : "inherit",
                  cursor: "pointer",
                  overflow: "hidden",
                  border: isCoach ? "2px solid #ff0000" : "none",
                }}
                key={client.id}
                onClick={() => setActiveClient(client)}
              >
                <Typography
                  color={activeClient?.id === client.id ? "white" : "inherit"}
                  sx={{
                    "& .email": {
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <b>
                    {client.name}
                    {isCoach && " (Me)"}
                  </b>
                  <br />
                  <span className="email">{client.email}</span>
                </Typography>
              </Paper>
            );
          })
        ) : searchQuery ? (
          <Paper sx={{ m: 2, p: 2 }}>
            <Typography sx={{ textAlign: "center", color: "text.secondary" }}>
              No clients found matching "{searchQuery}"
            </Typography>
          </Paper>
        ) : (
          <Paper sx={{ m: 2, p: 2 }}>
            <Box>
              <Typography sx={{ textAlign: "center", color: "red" }}>
                <b>You have no clients!</b>
              </Typography>
              <Typography sx={{ mt: 2, mb: 3, textAlign: "center" }}>
                Click above to invite a client to join you on Trainer App.
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

const Clients = () => {
  const { getClients, getCoachSubscription } = useApi();
  const { id: coachId, name: coachName, email: coachEmail } = useUserInfo();
  const isPlatformUser = coachId === PLATFORM_OWNER_USER_ID;
  const [clients, setClients] = useState([]);
  const [coachProfile, setCoachProfile] = useState(null);
  const [openAddClient, setOpenAddClient] = useState(false);
  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeClient, setActiveClient] = useState(null);
  const [productsDialogOpen, setProductsDialogOpen] = useState(false);
  const [clientHeaderActions, setClientHeaderActions] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const theme = useTheme();

  const clientCount = clients.length;

  const handleAddClientClick = async () => {
    if (isPlatformUser) {
      setOpenAddClient(true);
      return;
    }
    try {
      const sub = await getCoachSubscription();
      if (sub?.error) throw new Error(sub.error);

      // One free client when no subscription; subscribe for more
      if (!sub?.hasSubscription) {
        if (clientCount >= 1) {
          setOpenSubscriptionModal(true);
          return;
        }
        setOpenAddClient(true);
        return;
      }

      const slots = sub?.slots ?? 0;
      if (clientCount >= slots) {
        setSubscription(sub);
        setOpenUpgradeModal(true);
        return;
      }

      setOpenAddClient(true);
    } catch (err) {
      console.error("Failed to check subscription:", err);
      setOpenAddClient(true);
    }
  };

  const handleSubscriptionSuccess = () => {
    setOpenSubscriptionModal(false);
    localStorage.removeItem("userInfo");
    window.location.reload();
  };

  const handleUpgradeSuccess = () => {
    setOpenUpgradeModal(false);
    setSubscription(null);
    setOpenAddClient(true);
  };

  const fetchClients = async () => {
    setLoading(true);
    const c = await getClients();
    const activeClients = c.filter((client) => !client.deleted);
    setClients(activeClients);

    // Create coach profile object
    if (coachId && coachName) {
      const coach = {
        id: coachId,
        name: coachName,
        email: coachEmail || "",
        isCoach: true,
      };
      setCoachProfile(coach);
      // Set coach as active client by default if no active client
      if (!activeClient) {
        setActiveClient(coach);
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchClients();
  }, [coachId, coachName]);

  if (loading) {
    return (
      <Container sx={{ mt: 10 }}>
        <Loading />
      </Container>
    );
  }

  const SIDEBAR_WIDTH = 320;

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: 0,
        height: "calc(100vh - 72px)",
        mt: 1,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: SIDEBAR_WIDTH,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          borderRight: 1,
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <ClientsList
          clients={clients}
          coachProfile={coachProfile}
          activeClient={activeClient}
          setActiveClient={setActiveClient}
          onAddClientClick={handleAddClientClick}
          theme={theme}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "auto",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {activeClient && (
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {activeClient.name}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {clientHeaderActions}
            </Box>
          </Box>
          {activeClient && (
            <ClientWorkoutCalendar
              clientId={activeClient.id}
              onClientDeleted={() => {
                setActiveClient(null);
                fetchClients();
              }}
              setHeaderActions={setClientHeaderActions}
            />
          )}
        </Box>
      </Box>

      {openAddClient && (
        <AddClient
          onClose={() => setOpenAddClient(false)}
          onAddClient={() => {
            fetchClients();
          }}
        />
      )}
      {openSubscriptionModal && (
        <Elements
          stripe={stripePromise}
          options={{
            appearance,
            fonts: [
              {
                family: "Roboto Mono, monospace",
                cssSrc:
                  "https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@100;400;500;700",
              },
            ],
          }}
        >
          <CoachSubscriptionModal
            open={openSubscriptionModal}
            onClose={() => setOpenSubscriptionModal(false)}
            onSuccess={handleSubscriptionSuccess}
          />
        </Elements>
      )}
      {openUpgradeModal && subscription && (
        <CoachUpgradeModal
          open={openUpgradeModal}
          onClose={() => {
            setOpenUpgradeModal(false);
            setSubscription(null);
          }}
          currentSlots={subscription.slots}
          currentPaymentsPro={subscription.paymentsPro}
          clientCount={clientCount}
          subscriptionId={subscription.subscriptionId}
          onSuccess={handleUpgradeSuccess}
        />
      )}
      <ManageProductsDialog
        open={productsDialogOpen}
        onClose={() => setProductsDialogOpen(false)}
        client={activeClient}
      />
    </Box>
  );
};

export default Clients;
